import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, ListFilter, Plus, X, Bookmark, ChevronLeft, Trash2, LayoutList, UserCheck } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  LIST_FILTER_KEYS, FILTER_LABELS, filterOptionsFor, valueLabel,
  FilterValueList, DateRangeControl, AmountRangeControl,
} from "./FilterControls";
import { fmtDate } from "@/lib/api";
import { toast } from "sonner";

const buildChips = (filters, options) => {
  const chips = [];
  LIST_FILTER_KEYS.forEach((key) => {
    const vals = filters[key] || [];
    if (!vals.length) return;
    const labels = vals.map((v) => valueLabel(key, v, options));
    chips.push({
      key,
      label: `${FILTER_LABELS[key]}: ${labels[0]}${vals.length > 1 ? ` +${vals.length - 1}` : ""}`,
      values: labels,
    });
  });
  if (filters.date_from || filters.date_to) {
    const f = filters.date_from ? fmtDate(filters.date_from) : "…";
    const t = filters.date_to ? fmtDate(filters.date_to) : "…";
    chips.push({ key: "date", label: `Date: ${f} – ${t}` });
  }
  if (filters.amount_min || filters.amount_max) {
    const parts = [];
    if (filters.amount_min) parts.push(`≥ $${Number(filters.amount_min).toLocaleString()}`);
    if (filters.amount_max) parts.push(`≤ $${Number(filters.amount_max).toLocaleString()}`);
    chips.push({ key: "amount", label: `Amount: ${parts.join(" ")}` });
  }
  return chips;
};

export const FilterBar = ({
  search, onSearch, searchRef,
  filters, onFilterChange, onClearFilter, options,
  views, onApplyView, onSaveView, onDeleteView,
  saveOpen, setSaveOpen, total,
  assignedView, onToggleAssigned,
}) => {
  const [addStep, setAddStep] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [searchOpen, setSearchOpen] = useState(() => !!search);
  const chips = buildChips(filters, options);
  const scrollRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflowing(el.scrollWidth > el.clientWidth + 4);
  }, [chips.length, filters]);

  const toggleListValue = (key, value) => {
    const cur = filters[key] || [];
    onFilterChange(key, cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
  };

  const saveView = () => {
    const name = viewName.trim();
    if (!name) { toast.error("Give the view a name"); return; }
    onSaveView(name);
    setViewName("");
    setSaveOpen(false);
  };

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {/* search — collapsed to an icon until needed */}
      <div className="relative flex shrink-0 items-center">
        {!searchOpen && (
          <button onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 30); }}
            data-testid="expenses-search-icon" title="Search expenses ( / )"
            className="flex h-8 w-8 items-center justify-center rounded-md bg-[#141414] text-[#A3A3A3] transition-colors hover:bg-[#1f1f1f] hover:text-white">
            <Search size={14} />
          </button>
        )}
        <div className={searchOpen ? "relative" : "absolute h-0 w-0 overflow-hidden"}>
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <Input ref={searchRef} value={search} onChange={(e) => onSearch(e.target.value)}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => { if (!search) setSearchOpen(false); }}
            placeholder="Search…  /" data-testid="expenses-search-input"
            className="h-8 w-44 border-[#222] bg-[#111] pl-8 text-[12.5px] text-white placeholder:text-[#555]" />
        </div>
      </div>

      {/* assigned-to view toggle */}
      <button onClick={onToggleAssigned} data-testid="assigned-view-toggle"
        title="Group the table by controller — see who's working on what"
        className={`flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[12px] transition-colors ${
          assignedView ? "bg-[rgba(79,142,247,0.16)] text-[#7FAEF9]" : "bg-[#141414] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white"
        }`}>
        <UserCheck size={13} /> Assigned to
      </button>

      {/* filter icon */}
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${chips.length ? "bg-[rgba(79,142,247,0.14)] text-[#7FAEF9]" : "bg-[#141414] text-[#555]"}`}
        data-testid="filter-indicator" title={chips.length ? `${chips.length} filters active` : "No filters"}>
        <ListFilter size={14} />
      </span>

      {/* add filter — pinned left next to the filter icon */}
      <Popover open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setAddStep(null); }}>
        <PopoverTrigger asChild>
          <button data-testid="add-filter-button" title="Add filter"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#141414] text-[#A3A3A3] transition-colors hover:bg-[#1f1f1f] hover:text-white">
            <Plus size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 border-[#242424] bg-[#161616] p-2 text-white" data-testid="add-filter-popover">
          {!addStep ? (
            <>
              <div className="px-2 pb-1.5 pt-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Filter by</div>
              {[...LIST_FILTER_KEYS, "date", "amount"].map((key) => (
                <button key={key} onClick={() => setAddStep(key)} data-testid={`add-filter-${key}`}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-[12px] text-[#e5e5e5] hover:bg-[#1c1c1c]">
                  {FILTER_LABELS[key]}
                  {(filters[key]?.length || (key === "date" && (filters.date_from || filters.date_to)) || (key === "amount" && (filters.amount_min || filters.amount_max))) ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-[#4F8EF7]" />
                  ) : null}
                </button>
              ))}
            </>
          ) : (
            <>
              <button onClick={() => setAddStep(null)} className="mb-1.5 flex items-center gap-1 px-1 text-[11px] text-[#6b6b6b] hover:text-white">
                <ChevronLeft size={11} /> {FILTER_LABELS[addStep]}
              </button>
              {addStep === "date" ? (
                <DateRangeControl from={filters.date_from} to={filters.date_to}
                  onChange={(f, t) => { onFilterChange("date_from", f); onFilterChange("date_to", t); }} testPrefix="add" />
              ) : addStep === "amount" ? (
                <AmountRangeControl min={filters.amount_min} max={filters.amount_max}
                  onApply={(lo, hi) => { onFilterChange("amount_min", lo); onFilterChange("amount_max", hi); setAddOpen(false); setAddStep(null); }} testPrefix="add" />
              ) : (
                <FilterValueList options={filterOptionsFor(addStep, options)} selected={filters[addStep] || []}
                  onToggle={(v) => toggleListValue(addStep, v)} testPrefix="add" />
              )}
            </>
          )}
        </PopoverContent>
      </Popover>

      {/* saved views menu — pinned left next to add filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="views-menu-button" title="Saved views"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#141414] text-[#A3A3A3] transition-colors hover:bg-[#1f1f1f] hover:text-white">
            <LayoutList size={14} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 border-[#222] bg-[#161616] text-white" data-testid="views-menu">
          <div className="px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Saved views</div>
          {(views || []).length === 0 && (
            <div className="px-2 pb-2 text-[11.5px] text-[#555]">No saved views yet — apply filters, then hit the bookmark.</div>
          )}
          {(views || []).map((v) => (
            <DropdownMenuItem key={v.id} onClick={() => onApplyView(v)} data-testid={`view-item-${v.id}`}
              className="group flex items-center justify-between text-[12px] focus:bg-[#1f1f1f] focus:text-white">
              <span className="truncate">{v.name}</span>
              <button onClick={(e) => { e.stopPropagation(); onDeleteView(v.id); }} data-testid={`view-delete-${v.id}`}
                className="ml-2 hidden h-5 w-5 items-center justify-center rounded text-[#6b6b6b] hover:text-[#F87171] group-hover:flex">
                <Trash2 size={11} />
              </button>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* chips carousel */}
      <div className="relative min-w-0 flex-1">
        <div ref={scrollRef} className="scrollbar-none flex items-center gap-1.5 overflow-x-auto" data-testid="filter-chips">
          {chips.map((c) => (
            <span key={c.key} data-testid={`filter-chip-${c.key}`}
              className="flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-[rgba(79,142,247,0.12)] pl-2.5 pr-1.5 text-[11.5px] text-[#cfe0fb]">
              {c.values && c.values.length > 1 ? (
                <Tooltip delayDuration={150}>
                  <TooltipTrigger asChild>
                    <span className="cursor-default" data-testid={`filter-chip-label-${c.key}`}>{c.label}</span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="border border-[#2a2a2a] bg-[#1c1c1c] px-3 py-2 text-white"
                    data-testid={`filter-chip-tooltip-${c.key}`}>
                    <div className="text-[9.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">{FILTER_LABELS[c.key]} · {c.values.length} selected</div>
                    <ul className="mt-1 space-y-0.5">
                      {c.values.map((v) => <li key={v} className="text-[11.5px] text-[#e5e5e5]">{v}</li>)}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              ) : (
                c.label
              )}
              <button onClick={() => onClearFilter(c.key)} data-testid={`filter-chip-remove-${c.key}`}
                className="flex h-4 w-4 items-center justify-center rounded text-[#6b6b6b] hover:bg-[rgba(255,255,255,0.1)] hover:text-white">
                <X size={10} />
              </button>
            </span>
          ))}
          {chips.length === 0 && <span className="text-[11.5px] text-[#494949]">No filters — click a column header or the + to add</span>}
        </div>
        {overflowing && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#0A0A0A] to-transparent" data-testid="filter-chips-fade" />
        )}
      </div>

      {/* save as view */}
      {chips.length > 0 && (
        <Popover open={saveOpen} onOpenChange={setSaveOpen}>
          <PopoverTrigger asChild>
            <button data-testid="save-view-button" title="Save as view"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[rgba(79,142,247,0.14)] text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.28)] hover:text-white">
              <Bookmark size={13} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 border-[#242424] bg-[#161616] p-3 text-white" data-testid="save-view-popover">
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Save as view</div>
            <p className="mt-1 text-[11px] text-[#6b6b6b]">{chips.length} filter{chips.length > 1 ? "s" : ""} · {total} matching expenses</p>
            <Input value={viewName} onChange={(e) => setViewName(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") saveView(); }}
              placeholder="e.g. Big engineering spend" data-testid="save-view-name-input"
              className="mt-2 h-8 border-[#2a2a2a] bg-[#111] text-[12.5px] text-white placeholder:text-[#555]" />
            <button onClick={saveView} data-testid="save-view-confirm"
              className="mt-2 w-full rounded-lg btn-gradient py-1.5 text-[12px] font-medium text-white">
              Save view
            </button>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
